/**
 * Speech-to-Text (STT) utility
 *
 * Uses the browser's built-in SpeechRecognition API (free, no API key needed).
 * Supported in Chrome, Edge, and Safari (with webkit prefix).
 *
 * Supports Turkish (tr-TR) and English (en-US).
 */

type STTState = 'idle' | 'listening' | 'error';

type STTListener = (state: STTState) => void;

type STTResultListener = (transcript: string, isFinal: boolean) => void;

// Minimal type definitions for SpeechRecognition (not in standard TS lib)
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: { new (): SpeechRecognitionInstance };
    webkitSpeechRecognition?: { new (): SpeechRecognitionInstance };
  }
}

class STTService {
  private recognition: SpeechRecognitionInstance | null = null;
  private listeners: Set<STTListener> = new Set();
  private resultListeners: Set<STTResultListener> = new Set();
  private state: STTState = 'idle';
  private enabled: boolean = false;
  private lang: string = 'tr-TR';
  private shouldRestart: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        this.recognition = new SR();
      }
    }
  }

  subscribe(listener: STTListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  onResult(listener: STTResultListener): () => void {
    this.resultListeners.add(listener);
    return () => this.resultListeners.delete(listener);
  }

  private setState(state: STTState) {
    this.state = state;
    this.listeners.forEach((listener) => listener(state));
  }

  isSupported(): boolean {
    return this.recognition !== null;
  }

  getState(): STTState {
    return this.state;
  }

  setLang(lang: string) {
    this.lang = lang === 'tr' ? 'tr-TR' : 'en-US';
    if (this.recognition) {
      this.recognition.lang = this.lang;
    }
  }

  /**
   * Start listening. Transcripts will be sent to result listeners.
   */
  start(lang?: string) {
    if (!this.recognition) {
      this.setState('error');
      return;
    }

    if (this.state === 'listening') return;

    if (lang) {
      this.setLang(lang);
    }

    this.shouldRestart = true;

    this.recognition.lang = this.lang;
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.maxAlternatives = 1;

    this.recognition.onstart = () => {
      this.setState('listening');
    };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript || '';
        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        this.resultListeners.forEach((listener) => listener(finalTranscript, true));
      }
      if (interimTranscript) {
        this.resultListeners.forEach((listener) => listener(interimTranscript, false));
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'no-speech' and 'aborted' are not critical errors
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('[STT] Error:', event.error);
        this.setState('error');
        this.shouldRestart = false;
      }
    };

    this.recognition.onend = () => {
      if (this.shouldRestart) {
        // Chrome stops after a period of silence; restart if still active
        try {
          this.recognition?.start();
        } catch {
          this.setState('idle');
        }
      } else {
        this.setState('idle');
      }
    };

    try {
      this.recognition.start();
    } catch {
      // Already started
    }
  }

  /**
   * Stop listening.
   */
  stop() {
    this.shouldRestart = false;
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch {
        // Ignore
      }
    }
    this.setState('idle');
  }

  /**
   * Toggle listening state.
   */
  toggle(lang?: string) {
    if (this.state === 'listening') {
      this.stop();
    } else {
      this.start(lang);
    }
  }
}

// Singleton instance
export const stt = new STTService();
export type { STTState };
